import { action, computed, makeObservable, observable } from 'mobx';
import uniq from 'lodash/uniq';
import flatten from 'lodash/flatten';
import isNull from 'lodash/isNull';
import orderBy from 'lodash/orderBy';

import { DISABLE_ITEM_PRESENTATION_FROM, MEDICAL_CASE_RELEASE_DATE } from '@patient/store/entry/constants';
import { FormulaCalculation, FormulaParser } from '@utils/formula-parser';

import {
    EntryAudioRecordData,
    EntryData,
    EntryEDSData,
    EntryTypeData,
    FileViewModel,
    InsuranceApprovalHistoryVersion,
    Nullable,
    Optional,
    ReferralStatus,
    ReservationData,
    SoapTypeData,
} from 'chaika-frontend-types/frontend-cms';
import { EntryStatus, EntryTypeFieldName, EntryTypeFieldType, EntryTypeType, Locale } from 'chaika-enums';

import { store } from '@store';
import { EntryType } from '@template/store/entryType/EntryType';
import { DiagnosisField, EntryFields, IField, TableField } from '@store/models/EntryFields';
import { IList } from '@store/abstract/List';
import { FileAsset } from '@store/models/FileAsset';
import { ReferralList, ReferralList as ReferralListStore } from '@store/models/SystemFields/Referral';
import { RenderedServiceList as RenderedServiceListStore } from '@store/models/SystemFields/RenderedService';
import { MedicalCaseList } from '@store/models/SystemFields/MedicalCase';
import { DrugList } from '@store/models/SystemFields/Drug';
import { ALWAYS_SHOW_VALUE_ENTRY_FIELD, getEntryTypeWithoutMedCaseTitles } from '@template/store/entryType/types';
import { AnyObject } from '@store/types';
import { OptionType } from '@components/Select';
import { DateHelper } from '@utils/DateHelper';
import { keyArrayFromIterable } from '@utils/keyArrayFromIterable';
import { assertIsDefined } from '@utils/assertIsDefined';

import { EntryVersionList } from './EntryVersionList';
import { EntryTypeItemPresentationList } from './EntryTypeItemPresentationList';
import { DrugHistoryList } from '@store/models/SystemFields/Drug/DrugHsitoryList';
import { EntryTranslationList } from '@patient/store/entry/EntryTranslations';
import { EntryEntryTypeHelper } from '@patient/services/entry/EntryEntryTypeHelper';
import { EntryExpensesAllocationViewModel, MaterialActViewModel } from 'chaika-types';

interface CreateEntryParams extends EntryData {
    selectedLang?: Locale;
}

export interface TabsParams {
    isWarning?: boolean;
}

export enum SoapEntryTypeNames {
    SubjectiveEntryType = 'subjectiveEntryType',
    AssessmentEntryType = 'assessmentEntryType',
    PlanEntryType = 'planEntryType',
    PaymentEntryType = 'paymentEntryType',
}

// Когда-то эти поля были сквозными и пони мчситается ИМТ, они стали обычными и ИМТ сломалось
const SHOULD_CHECK_LIKE_GLOBAL_NAMES: string[] = [EntryTypeFieldName.Height, EntryTypeFieldName.Weight];
export class Entry {
    public static Name = 'entry' as const;

    @observable public id: string;
    @observable public serialNumber: number;
    @observable public ehrId: string;
    @observable public typeId: string;
    @observable public typeVersion: number;
    @observable public author: string;
    @observable public authorId: string;
    @observable public doctorId?: string;
    @observable public coAuthorIds?: string[];
    @observable public note: string;
    @observable public inDoor: boolean;
    @observable public deviceId?: string;
    @observable public officeId?: string;
    @observable public insuranceApproval: string;
    @observable public insuranceApprovalVersions: InsuranceApprovalHistoryVersion[] = [];
    @observable public status: Optional<EntryStatus>;
    @observable public creationTime: string;
    @observable public visitTime?: string;
    @observable public signedAt?: string;
    @observable public updateTime: string;
    @observable public organizationId: string;
    @observable public isOnPrint = false;
    @observable public selectedVersion?: Nullable<string>;
    @observable public selectedLang?: Locale;
    @observable public fields: EntryFields = new EntryFields();
    @observable public originFields: EntryFields = new EntryFields();
    @observable public ehrFields: EntryFields = new EntryFields();
    @observable public entryVersions: EntryVersionList = new EntryVersionList();
    @observable public entryType: EntryType = new EntryType({} as EntryTypeData);
    @observable public soapType?: SoapTypeData;
    @observable public soapEntryType: Map<SoapEntryTypeNames, EntryType> = new Map();
    @observable public files: FileAsset<{ locale: Locale }>[] = [];
    @observable public availableReservations: ReservationData[] = [];
    @observable public reservation?: ReservationData;
    @observable public medicalCases = new MedicalCaseList();
    @observable public ehrMedicalCases = new MedicalCaseList();
    @observable public ehrMedicalCasesAllVersions = new MedicalCaseList();
    @observable public drugList = new DrugList();
    @observable public drugHistoryList: DrugHistoryList = new DrugHistoryList();
    @observable public entryTypeItemPresentationList = new EntryTypeItemPresentationList();
    @observable public isCalculatedScopeOnUpdate = false;
    @observable public eds?: EntryEDSData;
    @observable public translations = new EntryTranslationList();
    @observable public mobileShowRightMenu: boolean = false;
    @observable public materialAct?: MaterialActViewModel;
    @observable public entryExpensesAllocations: EntryExpensesAllocationViewModel[] = [];
    @observable public audioRecord?: EntryAudioRecordData;
    @observable public isAudioRecordLoaded: boolean = false;

    @observable public tabsParams = new Map<string, TabsParams>();

    @observable private viewMode: boolean = false;
    @observable private translationMode: boolean = false;
    @observable private isTreatmentPlanValid_: Optional<boolean>;
    @observable private isUrgent_: boolean = false;
    private formulaCalculation = new FormulaCalculation();

    public constructor(
        {
            id,
            ehrId,
            typeId,
            typeVersion,
            status,
            author,
            authorId,
            doctorId,
            coAuthorIds,
            creationTime,
            visitTime,
            organizationId,
            isUrgent,
            insuranceApproval = '',
            note = '',
            fields = [],
            updateTime,
            signedAt,
            selectedLang,
            deviceId,
            officeId,
            soapType,
            serialNumber,
            inDoor = true,
        }: CreateEntryParams = {} as CreateEntryParams,
    ) {
        makeObservable(this);

        this.id = id;
        this.serialNumber = serialNumber;
        this.ehrId = ehrId;
        this.typeId = typeId;
        this.doctorId = doctorId;
        this.typeVersion = typeVersion;
        this.status = status;
        this.creationTime = creationTime;
        this.updateTime = updateTime;
        this.visitTime = visitTime;
        this.signedAt = signedAt;
        this.author = author;
        this.authorId = authorId;
        this.coAuthorIds = coAuthorIds;
        this.organizationId = organizationId;
        this.note = note;
        this.insuranceApproval = insuranceApproval;
        this.isUrgent_ = isUrgent;
        this.inDoor = inDoor;
        this.selectedLang = selectedLang;
        this.deviceId = deviceId;
        this.officeId = officeId;
        this.soapType = soapType;

        this.fields = new EntryFields(fields);
        this.originFields = new EntryFields(fields);
    }

    @computed
    public get isDisabledEntryTypeItemPresentations(): boolean {
        return DateHelper.isAfter(this.visitTime ?? this.creationTime, DISABLE_ITEM_PRESENTATION_FROM);
    }

    @computed
    public get isSigned(): boolean {
        return this.status === EntryStatus.Signed;
    }

    @computed
    public get type(): EntryType {
        return this.entryType;
    }

    @computed
    public get computedFields(): Map<string, any> {
        const parser = new FormulaParser({
            fields: EntryEntryTypeHelper.entryFields,
            scope: this.fieldScope,
            entryCreationTime: new Date(this.creationTime),
        });
        const results = parser.compute();

        return new Map(results.map(item => [item.name, item.result]));
    }

    @computed
    public get changedComputedFields(): Map<string, any> {
        const fields = EntryEntryTypeHelper.entryFields;

        const scope: Map<string, any> = new Map(
            fields.map(({ name, global }) => {
                const value = global && !this.fields.getValue(name) ? this.ehrFields.getValue(name) : null;
                return [name, value];
            }),
        );

        const parser = new FormulaParser({
            fields: fields.map(({ name, type, formula }) => ({
                name,
                formula: type === EntryTypeFieldType.CustomComputed ? undefined : formula,
            })),
            scope,
            entryCreationTime: new Date(this.creationTime),
        });
        const results = parser.compute();

        const computedFields = this.computedFields;

        return new Map(
            results
                .filter(({ name, result }) => result !== computedFields.get(name))
                .map(({ name, result }) => [name, result]),
        );
    }

    @computed
    public get calculatedFieldsWishData(): Set<string> {
        const formulas = new Map<string, string>();
        const result = new Set<string>();
        EntryEntryTypeHelper.entryFields.forEach(field => {
            if (field.type === EntryTypeFieldType.CustomComputed && !!field.formula && field.formula !== '') {
                formulas.set(field.name, field.formula);
            } else if (field.type === EntryTypeFieldType.Table) {
                assertIsDefined(field.data.table, `Table field ${field.name} has table data`);
                const calculatedCells = TableField.getCalculatedFiledCells(field.data.table.cells);
                calculatedCells.forEach(({ name, formula }) => {
                    if (!!formula) {
                        formulas.set(TableField.makeCalculateKey(field.name, name), formula);
                    }
                });
            }
        });

        keyArrayFromIterable(formulas).forEach(name => {
            const formula = formulas.get(name);
            assertIsDefined(formula, `Formula of ${name} is collected above`);
            if (!!this.calculateValue(formula)) {
                result.add(name);
            }
        });

        return result;
    }

    @computed
    public get fieldNames(): string[] {
        const names = this.fields.fieldNames;

        return uniq([...names, ...this.entryVersions.fieldNames]);
    }

    @computed
    public get isViewMode(): boolean {
        return this.status === EntryStatus.Signed || this.status === EntryStatus.OnReview || this.viewMode;
    }

    @computed
    public get isTranslationMode(): boolean {
        return this.translationMode;
    }

    @computed
    public get isTreatmentPlanValid(): Optional<boolean> {
        return this.isTreatmentPlanValid_;
    }

    @computed
    public get isDescribeNeeded(): boolean {
        return (
            (this.entryType.type === EntryTypeType.Diagnostics || this.entryType.type === EntryTypeType.Tests) &&
            (this.status === EntryStatus.Draft || this.status === EntryStatus.Unsigned) &&
            !this.coAuthorIds?.length
        );
    }

    @computed
    public get isUrgent(): boolean {
        return this.isUrgent_;
    }

    @computed
    public get filesCount(): number {
        const { hasRootAccess } = store.authUser;
        return this.files.filter(file => (hasRootAccess ? true : !file.name.endsWith('.sgn'))).length;
    }

    @computed
    public get showNoMedicalCasesPopup(): boolean {
        const titleWithoutCase = getEntryTypeWithoutMedCaseTitles();
        return (
            !this.disableCheckMedicalCaseForSign &&
            this.entryType.type !== EntryTypeType.System &&
            titleWithoutCase.every(title => this.entryType.title !== title) &&
            !this.hasMedicalCases
        );
    }

    @computed
    public get showNoDiagnosesPopup(): boolean {
        const titleWithoutCase = getEntryTypeWithoutMedCaseTitles();
        return (
            !this.disableCheckMedicalCaseForSign &&
            this.entryType.type !== EntryTypeType.System &&
            titleWithoutCase.every(title => this.entryType.title !== title) &&
            !this.medicalCases.everyMedicalCaseHasDiagnoses &&
            !(this.entryType.type === EntryTypeType.Diagnostics || this.entryType.type === EntryTypeType.Tests)
        );
    }

    @computed
    public get hasRenderedServices(): boolean {
        const renderedServices = this.fields.getValue(EntryTypeFieldType.RenderedService) as RenderedServiceListStore;

        return (renderedServices.values || []).length > 0;
    }

    @computed
    public get renderedServicesList(): RenderedServiceListStore {
        return this.fields.getValue<RenderedServiceListStore>(EntryTypeFieldType.RenderedService);
    }

    @computed
    public get disableCheckMedicalCaseForSign(): boolean {
        return DateHelper.isBefore(this.creationTime, new Date(MEDICAL_CASE_RELEASE_DATE));
    }

    @computed
    public get hasMedicalCases(): boolean {
        return this.medicalCases.values.length > 0;
    }

    @computed
    public get hasMultipleMedicalCases(): boolean {
        return this.medicalCases.values.length > 1;
    }

    @computed
    public get medicalCasesEntryIds(): string[] {
        return uniq(flatten(this.medicalCases.values.map(value => value.relatedEntriesIds)));
    }

    @computed
    public get approvalVersions(): InsuranceApprovalHistoryVersion[] {
        return orderBy(this.insuranceApprovalVersions, 'versionDate', 'desc');
    }

    @computed
    public get isMedicalCasesHiddedOnPrint(): boolean {
        const type = this.entryType.type;

        return (
            type === EntryTypeType.Tests ||
            type === EntryTypeType.System ||
            type === EntryTypeType.Diagnostics ||
            type === EntryTypeType.UsDiagnostics
        );
    }

    public set isTreatmentPlanValid(value: Optional<boolean>) {
        this.isTreatmentPlanValid_ = value;
    }

    public set isViewMode(viewMode: boolean) {
        this.viewMode = viewMode;
    }

    public set isTranslationMode(translationMode: boolean) {
        this.translationMode = translationMode;
    }

    public isCanEditAndSign(id: string): boolean {
        return this.isAuthor(id) || this.isDoctor(id);
    }

    public isAuthor(id: string): boolean {
        return this.coAuthorIds?.includes(id) || this.authorId === id;
    }

    public isDoctor(id: string): boolean {
        return this.doctorId === id;
    }

    public tableHasCalculatedData(fieldName: string): boolean {
        const field = EntryEntryTypeHelper.getFieldByName(fieldName);
        const calculatedCells = field?.data.table
            ? TableField.getCalculatedFiledCells(field.data.table.cells)
            : undefined;

        return (
            !!calculatedCells &&
            calculatedCells.some(({ name }) =>
                this.calculatedFieldsWishData.has(TableField.makeCalculateKey(fieldName, name)),
            )
        );
    }

    @action
    public setShowMobileRightMenu(value: boolean) {
        this.mobileShowRightMenu = value;
    }

    @action
    public setTabsParams(key: string, value: TabsParams) {
        this.tabsParams.set(key, value);
    }

    @action
    public deleteTabsParams(key: string) {
        this.tabsParams.delete(key);
    }

    @action
    public setFields(fields: EntryFields): void {
        this.fields = fields;
    }

    @action
    public setStatus(status: EntryStatus): void {
        this.status = status;
    }

    @action
    public reset(): void {
        this.id = '';
        this.ehrId = '';
        this.typeId = '';
        this.selectedLang = undefined;
        this.status = undefined;
        this.creationTime = '';
        this.viewMode = false;
        this.isTranslationMode = false;
        this.organizationId = '';
        this.officeId = undefined;
        this.isTreatmentPlanValid_ = undefined;
        this.eds = undefined;
        this.materialAct = undefined;
        this.audioRecord = undefined;

        this.entryType.reset();
        this.fields.reset();
        this.originFields.reset();
        this.soapEntryType.clear();
    }

    public calculateValue(expr: string): any {
        if (!this.formulaCalculation.isInit) {
            this.formulaCalculation.initParser(this.fieldScope);
        }

        return expr !== '' ? this.formulaCalculation.calculate(expr) : undefined;
    }

    public isParentEntryForMedicalCase(medicalCaseId: string): boolean {
        const medicalCase = this.medicalCases.getExisting(medicalCaseId);

        return medicalCase.parentEntryId === this.id;
    }

    public getUniqFieldValuesUpdateTimes(name: string): string[] {
        return this.entryVersions.getUniqFieldValuesUpdateTime(name);
    }

    public getVersionField(updateTime: string, name: string): IField {
        const entry = this.entryVersions.getExisting(updateTime);

        return entry.getField(name);
    }

    public getField<V, U, I = string>(name: string): IField<V, U, I> {
        const {
            common: { fieldsParams },
        } = store;
        const isGlobal = name !== EntryTypeFieldName.DayOfMenstrualCycle && fieldsParams.get(name)?.global;
        const alwaysShowValue = ALWAYS_SHOW_VALUE_ENTRY_FIELD.includes(name);
        let field: IField;

        if (this.selectedVersion) {
            const entry = this.entryVersions.getExisting(this.selectedVersion);

            field = entry.getField(name);
        } else if (this.isViewMode && !alwaysShowValue) {
            field = this.fields.get(name);
        } else {
            const ehrField = this.ehrFields.get(name);
            const entryField = this.fields.get(name);

            let isDateNullValue = false;

            if (fieldsParams.get(name)?.type === EntryTypeFieldType.Date) {
                isDateNullValue = this.fields.isValueSet(name) && isNull(entryField.getValue());
            }

            field = isGlobal && !entryField.hasData && !isDateNullValue ? ehrField : entryField;
        }

        return field as any as IField<V, U, I>;
    }

    public hasDiagnosisField(): boolean {
        return !!this.entryType.getFieldByName(EntryTypeFieldName.MedicalCases);
    }

    public getEntryField<V, U, I = string>(name: string): IField {
        let field;

        if (this.selectedVersion) {
            const entry = this.entryVersions.getExisting(this.selectedVersion);

            field = entry.getField(name);
        } else {
            field = this.fields.get(name);
        }

        return field;
    }

    public getFieldValue<T>(name: string): T {
        let value;

        if (this.selectedVersion) {
            const entry = this.entryVersions.getExisting(this.selectedVersion);

            value = entry.getFieldValue<T>(name);
        } else {
            const field = this.fields.get(name);

            value = field ? field.getValue() : '';
        }

        return value;
    }

    public getEhrField(name: string): IField {
        return this.ehrFields.get(name);
    }

    public getEhrFieldValue<T>(name: string): T {
        const field = this.getEhrField(name);

        return field ? field.getValue() : ('' as T);
    }

    public getMedicalCaseOptions(
        additionalIds: string[] = [],
        includeEntryClosedDiagnosis?: boolean,
    ): OptionType<string>[] {
        const diagnosisField = this.getEntryField(EntryTypeFieldType.MedicalCases) as DiagnosisField;
        const ehrDiagnosisField = this.getEhrField(EntryTypeFieldType.MedicalCases) as DiagnosisField;

        const { openDiagnosisIds, ids } = diagnosisField.getValue();
        const entryDiagnosisIds = includeEntryClosedDiagnosis ? ids : openDiagnosisIds;
        const options = entryDiagnosisIds.map(id => ({
            value: id,
            label: diagnosisField.getItemTitle(id),
            menuLabel: `${diagnosisField.getItemTitle(id)} ${diagnosisField.getItemContent(id)}`,
        }));

        const additionalOptions = ehrDiagnosisField
            .getValue()
            .openDiagnosisIds.filter(id => additionalIds.includes(id) && !entryDiagnosisIds.includes(id))
            .map(id => ({
                value: id,
                label: ehrDiagnosisField.getItemTitle(id),
                menuLabel: `${ehrDiagnosisField.getItemTitle(id)} ${ehrDiagnosisField.getItemContent(id)}`,
            }));

        return additionalOptions.length ? options.concat(additionalOptions) : options;
    }

    public hasRenderedServiceForReferral(referralId: string): boolean {
        const referralList = this.fields.getValue(EntryTypeFieldType.Referrals) as ReferralList;
        const referral = referralList.values.find(({ id }) => id === referralId);
        const renderedServices = this.fields.getValue(EntryTypeFieldType.RenderedService) as RenderedServiceListStore;

        return !!renderedServices.values.find(({ serviceId }) => serviceId === referral?.serviceId);
    }

    @action
    public setSelectedLang(locale?: Locale): void {
        this.selectedLang = locale;
    }

    @action
    public setFieldValue(name: string | EntryTypeFieldName, value: any) {
        this.fields.set(name, value);

        const {
            common: { fieldsParams },
        } = store;
        const { global } = fieldsParams.get(name) || {};

        if (global) {
            this.ehrFields.set(name, value);
        }

        this.updateCaclulatedFieldScope();
    }

    @action
    public addListFieldValue(name: string | EntryTypeFieldName, value: any[]) {
        const list = this.fields.getValue<IList<any, any>>(name);

        list.add(value);

        const {
            common: { fieldsParams },
        } = store;
        const fieldParams = fieldsParams.get(name);
        assertIsDefined(fieldParams, `Field params of ${name} are loaded with the entry type`);
        const { global } = fieldParams;

        if (global) {
            const ehrList = this.ehrFields.getValue<IList<any, any>>(name);

            ehrList.add(value);
        }

        this.updateCaclulatedFieldScope();
    }

    @action
    public updateReferral(id: string, value: any): void {
        const list = this.fields.getValue<IList<any, any>>(EntryTypeFieldType.Referrals);

        list.update(id, value);
    }

    public hasReferralWithStatuses(serviceId: string, statuses: ReferralStatus[]): boolean {
        const referralList = this.fields.getValue(EntryTypeFieldType.Referrals) as ReferralListStore;
        const ehrReferralList = this.ehrFields.getValue(EntryTypeFieldType.Referrals) as ReferralListStore;

        const executeReferrals = [
            ...referralList.getFilteredValues({ serviceId, statuses }),
            ...referralList.getFilteredValues({ serviceIdFromCompositeService: serviceId, statuses }),
            ...ehrReferralList.getFilteredValues({ serviceId, statuses }),
            ...ehrReferralList.getFilteredValues({ serviceIdFromCompositeService: serviceId, statuses }),
        ];

        return executeReferrals.length > 0;
    }

    @action
    public updateListFieldItem(name: string, id: string, value: any) {
        const list = this.fields.getValue<IList<any, any>>(name);

        list.update(id, value);

        const {
            common: { fieldsParams },
        } = store;
        const fieldParams = fieldsParams.get(name);
        assertIsDefined(fieldParams, `Field params of ${name} are loaded with the entry type`);
        const { global } = fieldParams;

        if (global) {
            const ehrList = this.ehrFields.getValue<IList<any, any>>(name);

            ehrList.update(id, value);
        }

        this.updateCaclulatedFieldScope();
    }

    @action
    public addFile(file: FileViewModel) {
        const fileAsset = new FileAsset<{ locale: Locale }>(file);
        this.files = [...this.files, fileAsset];
        store.entryPage.rightMenu.hasFiles = true;
    }

    @action
    public deleteFile(name: string) {
        this.files = this.files.filter(file => file.name !== name);
        store.entryPage.rightMenu.hasFiles = !!this.files.length;
    }

    @action
    public deleteFileByPath(path: string) {
        this.files = this.files.filter(file => file.path !== path);
        store.entryPage.rightMenu.hasFiles = !!this.files.length;
    }

    @action
    public changeUrgency(isUrgent?: boolean): boolean {
        this.isUrgent_ = isUrgent ?? !this.isUrgent_;
        return this.isUrgent_;
    }

    @action
    public setEDS(eds?: EntryEDSData) {
        this.eds = eds;
    }

    @computed
    private get fieldScope(): Map<string, any> {
        const scope = EntryEntryTypeHelper.entryFields.map(({ name, type, global }) => {
            let value;
            const result: [string, any][] = [];
            if (global && type === EntryTypeFieldType.Date) {
                const filedValue = this.fields.getValue(name);
                value = !!filedValue || isNull(filedValue) ? filedValue : this.ehrFields.getValue(name);
            } else {
                value =
                    (global && name !== EntryTypeFieldName.DayOfMenstrualCycle) ||
                    (!this.isSigned && SHOULD_CHECK_LIKE_GLOBAL_NAMES.includes(name)) ||
                    (this.isSigned && SHOULD_CHECK_LIKE_GLOBAL_NAMES.some(name => this.fields.hasValue(name)))
                        ? this.fields.getValue(name) || this.ehrFields.getValue(name)
                        : this.fields.getValue(name);
            }
            if (!!value && type === EntryTypeFieldType.Table) {
                for (const cellName in value as AnyObject) {
                    result.push([TableField.makeCalculateKey(name, cellName), (value as AnyObject)[cellName]]);
                }
            }
            result.push([name, value]);

            return result;
        });

        return new Map(flatten(scope));
    }

    @action
    private updateCaclulatedFieldScope(): void {
        this.isCalculatedScopeOnUpdate = true;
        if (!this.formulaCalculation.isInit) {
            this.formulaCalculation.initParser(this.fieldScope);
        } else {
            this.formulaCalculation.updateScope(this.fieldScope);
        }
        this.isCalculatedScopeOnUpdate = false;
    }
}

export const entry = new Entry();
